"use client"

import type React from "react"
import { Card, CardContent, CardHeader } from "./card"
import { Button } from "./button"
import { X } from "lucide-react"

interface Career {
  title: string
  description: string
  salary: string
  education: string
  funFact: string
  growth: string
  match: number
  icon: React.ReactNode
}

interface CareerModalProps {
  career: Career | null
  isOpen: boolean
  onClose: () => void
}

export const CareerModal: React.FC<CareerModalProps> = ({ career, isOpen, onClose }) => {
  if (!isOpen || !career) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Blurry Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
        onClick={onClose}
      />
      
      {/* Modal Content */}
      <div className="relative bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-in fade-in-90 zoom-in-90 duration-300">
        <Card className="border-0 shadow-none">
          <CardHeader>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                  {career.icon}
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-900">{career.title}</h3>
                  <p className="text-base text-slate-600">{career.description}</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="rounded-full hover:bg-slate-100"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="flex flex-wrap gap-2">
              <div className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold">
                {career.match}% Match
              </div>
              <div className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-semibold">
                {career.growth}
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <h4 className="font-semibold text-slate-700 mb-2">Salary Range</h4>
                <p className="text-slate-900 font-medium">{career.salary}</p>
              </div>
              
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <h4 className="font-semibold text-slate-700 mb-2">Education Required</h4>
                <p className="text-slate-900">{career.education}</p>
              </div>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h4 className="font-semibold text-amber-800 mb-2">Fun Fact</h4>
              <p className="text-amber-700">{career.funFact}</p>
            </div>
            
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <h4 className="font-semibold text-indigo-800 mb-2">Why This Career Fits You</h4>
              <p className="text-indigo-700">
                Based on your assessment results, this career aligns with your creative problem-solving 
                skills and preference for innovative work environments. The growth opportunities and 
                dynamic nature of this field match your personality traits perfectly.
              </p>
            </div>
            
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
              <h4 className="font-semibold text-teal-800 mb-2">Next Steps</h4>
              <ul className="text-teal-700 list-disc list-inside space-y-1">
                <li>Research degree programs in related fields</li>
                <li>Explore internship opportunities</li>
                <li>Connect with professionals in this industry</li>
                <li>Build relevant skills through online courses</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}